import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { AxiosError } from 'axios';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import EmailBodyEditor from '../../scheduledEmails/components/EmailBodyEditor';
import { useSendOneOffEmail } from '../hooks/useSendOneOffEmail';
import { CollectionInvoiceDocument } from '../model';
import InvoicePickerModal, { SelectedInvoice } from './InvoicePickerModal';

interface Props {
  open: boolean;
  clientId: number;
  toEmail: string;
  contactName?: string | null;
  sendingDisabled: boolean;
  invoiceDocuments: CollectionInvoiceDocument[];
  onClose: () => void;
  onSent: (info: { toEmail: string; subject: string }) => void;
}

const MAX_TOTAL_BYTES = 15 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt'];

const extOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
};

const bodyIsEmpty = (html: string): boolean => html.replace(/<[^>]*>/g, '').trim().length === 0;

const OneOffEmailModal: React.FC<Props> = ({
  open,
  clientId,
  toEmail,
  contactName,
  sendingDisabled,
  invoiceDocuments,
  onClose,
  onSent,
}) => {
  const { showErrorSnackBar } = useContext(SnackBarContext);
  const { sendOneOffEmail, isLoading } = useSendOneOffEmail();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [selectedInvoices, setSelectedInvoices] = useState<SelectedInvoice[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject('');
      setBody('');
      setFiles([]);
      setSelectedInvoices([]);
      setPickerOpen(false);
    }
  }, [open]);

  const handleAddFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';
    const bad = selected.find((f) => !ALLOWED_EXTENSIONS.includes(extOf(f.name)));
    if (bad) {
      showErrorSnackBar?.(`Unsupported file type: ${bad.name}`);
      return;
    }
    const next = [...files, ...selected];
    if (next.reduce((sum, f) => sum + f.size, 0) > MAX_TOTAL_BYTES) {
      showErrorSnackBar?.('Attachments exceed the 15 MB total limit.');
      return;
    }
    setFiles(next);
  };

  const removeFile = (index: number) => setFiles((prev) => prev.filter((_, i) => i !== index));
  const removeInvoice = (id: number) => setSelectedInvoices((prev) => prev.filter((inv) => inv.id !== id));

  const canSend = subject.trim().length > 0 && !bodyIsEmpty(body) && !isLoading && !sendingDisabled;

  const handleSend = async () => {
    try {
      await sendOneOffEmail({
        clientId,
        toEmail,
        subject: subject.trim(),
        body,
        attachments: files,
        invoicePdfIds: selectedInvoices.map((inv) => inv.id),
      });
      onSent({ toEmail, subject: subject.trim() });
    } catch (err) {
      const axiosErr = err as AxiosError<{ error?: string }>;
      showErrorSnackBar?.(axiosErr.response?.data?.error || 'Failed to send email. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Send email{contactName ? ` — ${contactName}` : ''}</Typography>
        <IconButton onClick={onClose} size="small" disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {sendingDisabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Email sending is disabled for this location. This message can&apos;t be sent until it&apos;s re-enabled
            under Collections → Configuration.
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body2" color="textSecondary"><strong>To:</strong></Typography>
          <Chip label={toEmail} size="small" />
        </Box>
        <TextField
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          disabled={isLoading}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Message
        </Typography>
        <EmailBodyEditor initialContent="" onChange={setBody} showVariables={false} />
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button component="label" size="small" startIcon={<AttachFileIcon />} disabled={isLoading}>
            Add files
            <input type="file" hidden multiple onChange={handleAddFiles} />
          </Button>
          <Button size="small" startIcon={<ReceiptLongIcon />} disabled={isLoading} onClick={() => setPickerOpen(true)}>
            Attach invoices
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {selectedInvoices.map((inv) => (
            <Chip
              key={`invoice-${inv.id}`}
              icon={<ReceiptLongIcon />}
              label={`#${inv.apply_to}`}
              title={inv.filename}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => removeInvoice(inv.id)}
              deleteIcon={<DeleteOutlineIcon />}
            />
          ))}
          {files.map((file, index) => (
            <Chip
              key={`${file.name}-${index}`}
              label={file.name}
              size="small"
              variant="outlined"
              onDelete={() => removeFile(index)}
              deleteIcon={<DeleteOutlineIcon />}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={isLoading ? <CircularProgress size={16} /> : <SendIcon />}
          onClick={handleSend}
          disabled={!canSend}
        >
          Send
        </Button>
      </DialogActions>
      <InvoicePickerModal
        open={pickerOpen}
        documents={invoiceDocuments}
        initialSelectedIds={selectedInvoices.map((inv) => inv.id)}
        onClose={() => setPickerOpen(false)}
        onConfirm={(selected) => {
          setSelectedInvoices(selected);
          setPickerOpen(false);
        }}
      />
    </Dialog>
  );
};

export default OneOffEmailModal;
